import Quill from 'quill';
import { Subscription } from 'rxjs';
import { NgClass } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { AfterViewInit, Component, ElementRef, HostListener, input, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';

import { AppMaterialModule } from '../../../material.module';
import { _BaseControlComponent } from '../../components/_base-control.component';

// Toolbar = "basis + headings/quote": vet/cursief/onderstreept, kop-niveaus,
// geordende/ongeordende lijst, blockquote, link, een clean-knop en een fullscreen-toggle
// (custom knop: geen Quill-format, krijgt een handler + FA-icoon in __initQuill).
const RICH_TEXT_TOOLBAR = [
    ['bold', 'italic', 'underline'],
    [{ header: [1, 2, 3, false] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['blockquote'],
    ['link'],
    ['clean'],
    ['fullscreen'],
];

// Lege Quill-inhoud serialiseert naar dit fragment; we slaan dat op als null
// zodat een "leeg" veld ook echt leeg is (validatie/required werkt dan correct).
const QUILL_EMPTY_HTML = '<p><br></p>';

@Component({
    selector: 'app-rich-text-control',
    templateUrl: './rich-text-control.component.html',
    styleUrls: [
        './rich-text-control.component.scss',
        '../../control.scss',
    ],
    standalone: true,
    host: {
        '[class.readonly]': 'readonly$$()'
    },
    imports: [
        AppMaterialModule,
        NgClass,
        ReactiveFormsModule,
    ],
})
export class RichTextControlComponent
    extends _BaseControlComponent
    implements OnInit, AfterViewInit, OnDestroy
{
    // private properties
    private __quill?: Quill;
    private __statusSubscr?: Subscription;
    private __valueSubscr?: Subscription;
    private __syncing = false; // guard tegen echo tussen editor ↔ FormControl
    private __fullscreenBtn: HTMLButtonElement | null = null;
    // Oorspronkelijke plek van het veld, om na fullscreen exact terug te kunnen plaatsen.
    private __fieldHome: { parent: HTMLElement; next: Node | null } | null = null;

    // override properties
    override get label() { return this.input_label$$(); }

    override attributes$$        = this._input_attributes$$;
    override autofocus$$         = this._input_autofocus$$;
    override control$$           = this._control$$;
    override controlName$$       = this._input_controlName$$;
    override disabled$$          = this._input_disabled$$;
    override errorShow$$         = this._input_errorShow$$;
    override errorStateMatcher$$ = this._input_errorStateMatcher$$;
    override id$$                = this._input_id$$;
    override lockOnLoad$$        = this._input_lockOnLoad$$;
    override placeholder$$       = this._input_placeholder$$;
    override readonly$$          = this._input_readonly$$;
    override subscriptHide$$     = this._input_subscriptHide$$;
    override tabIndex$$          = this._tabIndex$$;
    override value$$             = this._input_value$$;
    override valueReset$$        = this._input_valueReset$$;

    // public properties
    input_label$$     = input<string>('',         { alias: 'label' });
    input_minHeight$$ = input<string>('160px',    { alias: 'minHeight' });
    input_maxHeight$$ = input<string | null>(null, { alias: 'maxHeight' });

    // Editor als in-app overlay over de hele viewport (zelfde patroon als de fullscreen
    // foto-editor in draw-image); getoggled via de fullscreen-knop in de Quill-toolbar.
    fullscreen$$ = signal(false);

    @ViewChild('editorHost') editorHost?: ElementRef<HTMLElement>;
    @ViewChild('field') fieldRef?: ElementRef<HTMLElement>;

    // Escape verlaat eerst het volledige scherm (en niets anders); buiten fullscreen
    // blijft escape van de omliggende machinerie (form/tab-page) ongemoeid.
    @HostListener('document:keydown.escape', ['$event']) emit_fullscreenEscape(event: Event): void {
        if (!this.fullscreen$$()) { return; }
        event.preventDefault();
        event.stopPropagation();
        this.__fullscreen_set(false);
    }

    // methods
    override ngOnInit(): void {
        super._ngOnInit();

        const _value = this._input_value$$();
        if (_value) { this.control$$().setValue(_value, { emitEvent: false }); }
    }

    ngAfterViewInit(): void {
        // In readonly-modus is er geen editor-host (template-@if): dan tonen we de
        // (door Angular gesaniteerde) HTML via [innerHTML], geen Quill nodig.
        if (this.readonly$$() || !this.editorHost) { return; }
        this.__initQuill(this.editorHost.nativeElement);
    }

    override errorMessage_getFirst(): string {
        return this._validationMessage(this.control$$()?.errors);
    }

    emit_fullscreenToggle(): void {
        this.__fullscreen_set(!this.fullscreen$$());
    }

    // In fullscreen verhuist het veld fysiek naar document.body (portal): binnen de
    // mat-tab zit het in een transform-/stacking-context waardoor position:fixed daar
    // gevangen blijft en de rest van de pagina bereikbaar bleef. Quill's DOM en de
    // Angular-bindings verhuizen gewoon mee; bij verlaten gaat het veld exact terug.
    private __fullscreen_set(value: boolean): void {
        this.fullscreen$$.set(value);
        const _el = this.fieldRef?.nativeElement ?? null;

        if (_el) {
            if (value && _el.parentElement) {
                this.__fieldHome = { parent: _el.parentElement, next: _el.nextSibling };
                document.body.appendChild(_el);
            } else if (!value && this.__fieldHome) {
                this.__fieldHome.parent.insertBefore(_el, this.__fieldHome.next);
                this.__fieldHome = null;
            }
        }

        this.__fullscreenIcon_update();
    }

    private __fullscreenIcon_update(): void {
        if (!this.__fullscreenBtn) { return; }

        const _full = this.fullscreen$$();
        this.__fullscreenBtn.innerHTML = `<i class="fas ${_full ? 'fa-compress' : 'fa-expand'}"></i>`;
        this.__fullscreenBtn.title = _full ? 'verlaat volledig scherm' : 'volledig scherm';
    }

    private __initQuill(host: HTMLElement): void {
        const _control = this.control$$();

        this.__quill = new Quill(host, {
            theme: 'snow',
            placeholder: this.placeholder$$(),
            modules: { toolbar: {
                container: RICH_TEXT_TOOLBAR,
                handlers: { fullscreen: () => this.emit_fullscreenToggle() },
            } },
        });

        // De fullscreen-knop is geen Quill-format: Quill rendert hem leeg — het (toestand-
        // afhankelijke) FA-icoon en de tooltip vullen we zelf.
        const _toolbarEl = (this.__quill.getModule('toolbar') as { container?: HTMLElement } | null)?.container ?? null;
        this.__fullscreenBtn = _toolbarEl?.querySelector<HTMLButtonElement>('button.ql-fullscreen') ?? null;
        this.__fullscreenIcon_update();

        // Begininhoud uit de FormControl in de editor zetten.
        const _initial = _control.value as string | null;
        if (_initial) { this.__quill.clipboard.dangerouslyPasteHTML(_initial); }

        // editor → FormControl
        this.__quill.on('text-change', () => {
            if (this.__syncing) { return; }
            const _html = this.__quill!.getLength() <= 1 ? null : this.__quill!.root.innerHTML;
            this.__syncing = true;
            // markAsDirty() vóór setValue(): FormComponent leest form.dirty synchroon in de
            // valueChanges-subscriptie die setValue() meteen afvuurt, dus bij de omgekeerde volgorde
            // bleef de Save-knop bij de EERSTE tekstwijziging disabled.
            _control.markAsDirty();
            _control.setValue(_html === QUILL_EMPTY_HTML ? null : _html);
            this.__syncing = false;
        });

        // FormControl → editor (externe reset/patch); niet bij eigen edits.
        this.__valueSubscr = _control.valueChanges.subscribe((value: string | null) => {
            if (this.__syncing) { return; }
            const _current = this.__quill!.getLength() <= 1 ? '' : this.__quill!.root.innerHTML;
            if ((value ?? '') === _current) { return; }
            this.__syncing = true;
            this.__quill!.clipboard.dangerouslyPasteHTML(value ?? '');
            this.__syncing = false;
        });

        // lockOnLoad/disabled spiegelen naar de editor (base disablet de FormControl).
        this.__quill.enable(!_control.disabled);
        this.__statusSubscr = _control.statusChanges.subscribe(() => {
            this.__quill!.enable(!_control.disabled);
        });

        if (this.autofocus$$()) { this.__quill.focus(); }
    }

    override ngOnDestroy(): void {
        // Veld eerst terugplaatsen: een naar body verplaatst element ruimt Angular bij
        // destroy niet zelf op (het hangt dan buiten de component-host).
        if (this.fullscreen$$()) { this.__fullscreen_set(false); }

        super._ngOnDestroy();
        this.__statusSubscr?.unsubscribe();
        this.__valueSubscr?.unsubscribe();
        this.__quill = undefined;
    }
}
